import { useContext, useLayoutEffect } from "react";
import {
  DEFAULT_DASH_LANGUAGE,
  DashLanguages,
  firstSupportedDashLanguage,
  normalizeDashLanguage,
} from "dash-common";
import { i18next, ViewerContext } from "dash-ui";

export const MULTILINGUAL_ENABLED =
  process.env["REACT_APP_MULTILINGUAL_ENABLED"] === "true";

const detectBrowserLanguage = () =>
  typeof navigator === "undefined"
    ? undefined
    : firstSupportedDashLanguage(
        navigator.languages?.length ? navigator.languages : [navigator.language]
      );

const resolveLanguage = (
  signedIn: boolean,
  accountLanguage?: string | null
): DashLanguages => {
  if (!MULTILINGUAL_ENABLED) return DEFAULT_DASH_LANGUAGE;
  if (signedIn) {
    const fromAccount = normalizeDashLanguage(accountLanguage);
    if (fromAccount) return fromAccount;
  }
  return detectBrowserLanguage() ?? DEFAULT_DASH_LANGUAGE;
};

/** The account language when the account carries one, else the browser language. A no-op while the flag is off. */
export function usePortalLanguage() {
  const { account } = useContext(ViewerContext);
  const signedIn = !!account?._id;
  const accountLanguage = account?.language ?? null;

  // Layout effect: switch before the first paint of a freshly loaded account.
  useLayoutEffect(() => {
    if (!MULTILINGUAL_ENABLED) return;
    const language = resolveLanguage(signedIn, accountLanguage);
    // Must be dash-ui's instance: an app-side `import i18next from "i18next"` is a separate, uninitialised copy.
    if (i18next.language !== language) void i18next.changeLanguage(language);
    document.documentElement.lang = language;
  }, [signedIn, accountLanguage]);
}
